import { ChatEmojiService } from '@agent/agent-interface/contact-view/chat/services/chat-emoji.service';
import { ChatMessageUtilService } from '@agent/agent-interface/contact-view/chat/services/chat-message-util.service';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ChatCopyGuest } from '@commons/chat-assistance/models/chat-copy-guest';
import { Subscription } from 'rxjs';

import { Logger, Option } from '@contact/upgrade';

import { ChatAssistanceService } from '../chat-assistance.service';
import { IChatGuest } from '../models/chat-guest';
import { IChatMessage } from '../models/chat-message';

@Component({
  selector: 'chat-room',
  templateUrl: 'chat-room.component.html',
  styleUrls: ['./chat-room.component.scss'],
})
export class ChatRoomComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  roomid: string;

  messages: IChatMessage[] = [];
  copyMessages: Map<string, any> = new Map<string, any>();
  messageToSend: string = '';
  guestToAdd: IChatGuest | null;
  isChanged: Boolean = false;

  @Output()
  onRoomClosed: EventEmitter<any> = new EventEmitter();

  @Output()
  onAddGuestToRoom: EventEmitter<any> = new EventEmitter();

  private guestsDirectory: IChatGuest[] = [];
  private disposable: Subscription;

  constructor(
    private readonly logger: Logger,
    private readonly chatAssistanceService: ChatAssistanceService,
    private readonly chatEmojiService: ChatEmojiService,
    private readonly chatMessageUtilService: ChatMessageUtilService
  ) {}

  get guests(): IChatGuest[] {
    return this.chatAssistanceService.getGuests(this.roomid);
  }

  get availableGuests() {
    return this.guestsDirectory
      .filter(guest => guest.isPresent)
      .filter(guest => this.chatAssistanceService.canAddGuest(guest))
      .filter(guest => this.guests.map(g => g.id).indexOf(guest.id) < 0);
  }

  ngOnInit(): void {
    this.logger.info(`Selected room id = ${this.roomid}`);

    this.disposable = this.chatAssistanceService.open(this.roomid).subscribe(message => {
      if (!message.copyGuest || (message.copyGuest && message.copyGuest.isFullCopy)) {
        this.messages.push(message);
      } else if (message.copyGuest.copyKey) {
        const copy = this.copyMessages.get(message.copyGuest.copyKey);
        if (!copy) {
          this.copyMessages.set(message.copyGuest.copyKey, [message]);
        } else {
          this.copyMessages.get(message.copyGuest.copyKey).push(message);
        }
      }

      //to know if messages change, based on this we activate the scroll to bottom
      this.isChanged = true;
    });

    this.chatAssistanceService.getGuestsDirectory().subscribe(directory => {
      this.guestsDirectory = directory;
    });
  }

  ngOnDestroy(): void {
    Option.of(this.disposable).forEach(disposable => disposable.unsubscribe());
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.roomid = changes['roomid'].currentValue;
    Option.of(changes['roomid'])
      .filter(changeObj => !changeObj.isFirstChange())
      .forEach(() => {
        this.resetComponent();
        this.ngOnInit();
      });
  }

  onKeyPressed(event): boolean {
    if (this.messageToSend.length > 0 && event.key === 'Enter') {
      event.preventDefault();
      this.sendMessage();
      return false;
    }
    return true;
  }

  sendMessage(): void {
    this.chatAssistanceService.send(this.roomid, this.messageToSend);
    this.messageToSend = '';
  }

  canAddGuest(): boolean {
    return this.guests.length < 1 || this.availableGuests.length > 0;
  }

  addGuest(guest: IChatGuest): void {
    if (guest) {
      this.chatAssistanceService.addGuest(this.roomid, guest);
      this.guestToAdd = null;
    }
  }
  addGuestToRoom() {
    this.onAddGuestToRoom.emit(this.roomid);
  }

  msgId($index: number): string {
    // if this is the last message and there is a change in messages
    if ($index === this.messages.length - 1 && this.isChanged) {
      setTimeout(() => {
        this.scrollToBottom(); // trigger scroll to bottom
      }, 100);
      this.isChanged = false;
    }
    return `message-${$index}`;
  }

  emptyConversation(): void {
    this.messages = [];
    this.chatAssistanceService.deleteContentConversation(this.roomid);
  }

  closeRoom(): void {
    this.onRoomClosed.emit(this.roomid);
  }

  getMessageContent(message: string): string {
    if (message) {
      let text = this.chatMessageUtilService.processContent(message);
      return this.chatEmojiService.emojify(text);
    }
    return '';
  }

  isFromUnknownContact(message: IChatMessage) {
    return message.copyGuest && ChatCopyGuest.isFromUnknownContact(message.copyGuest);
  }

  messageClassName(message) {
    return message.isFullCopy ? '' : message.direction;
  }

  private resetComponent(): void {
    this.logger.debug(`Resetting component (roomid = ${this.roomid})`);
    Option.of(this.disposable).forEach(disposable => disposable.unsubscribe());
    this.messages = [];
    this.messageToSend = '';
  }

  private scrollToBottom(): void {
    let messagesElement = document.getElementsByClassName('chatPanel-content').item(0);
    if (messagesElement) {
      messagesElement.scrollTop = messagesElement.scrollHeight;
    }
  }
}
